import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryPage = () => {
  const [gallery, setGallery] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [galleryRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/gallery`),
          axios.get(`${API}/gallery/categories`),
        ]);
        setGallery(galleryRes.data);
        setCategories(["All", ...categoriesRes.data.categories]);
      } catch (error) {
        console.error("Error fetching gallery:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredGallery = activeCategory === "All"
    ? gallery
    : gallery.filter(g => g.category === activeCategory);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  // Get grid item class based on index for tetris layout
  const getGridClass = (index) => {
    const patterns = [
      "tetris-item-large",
      "tetris-item-small",
      "tetris-item-tall",
      "tetris-item-small",
      "tetris-item-small",
      "tetris-item-small"
    ];
    return patterns[index % patterns.length];
  };

  return (
    <main className="pt-20" data-testid="gallery-page">
      {/* Hero */}
      <section className="section-padding bg-[#050505]">
        <div className="container-luxury">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4">
              Our Work
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] font-bold mb-6 text-[#F5F5F0]">
              Event Gallery
            </h1>
            <p className="text-[#A1A1AA] text-lg leading-relaxed">
              A curated collection of our most memorable events. Each celebration 
              tells a unique story of elegance and artistry.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#0F0F0F] border-y border-[rgba(212,175,55,0.15)] sticky top-20 z-40">
        <div className="container-luxury">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 text-sm uppercase tracking-widest transition-all ${
                  activeCategory === category
                    ? "bg-[#D4AF37] text-[#050505]"
                    : "border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37]"
                }`}
                data-testid={`gallery-category-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding bg-[#050505]" data-testid="gallery-grid">
        <div className="container-luxury">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="tetris-grid">
              {filteredGallery.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`${getGridClass(index)} group cursor-pointer relative overflow-hidden`}
                  onClick={() => setSelectedImage(item)}
                  data-testid={`gallery-item-${item.id}`}
                >
                  <img
                    src={item.url}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="text-[#D4AF37] text-xs uppercase tracking-widest mb-2">
                        {item.category}
                      </p>
                      <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0]">
                        {item.title}
                      </h3>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-[#050505]/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="gallery-lightbox"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="max-w-5xl max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-[#D4AF37] hover:text-[#F5F5F0] transition-colors text-sm uppercase tracking-widest"
              data-testid="lightbox-close"
            >
              Close
            </button>
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="max-w-full max-h-[80vh] object-contain"
            />
            <div className="mt-6">
              <p className="text-[#D4AF37] text-xs uppercase tracking-widest mb-2">
                {selectedImage.category}
              </p>
              <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#F5F5F0] mb-2">
                {selectedImage.title}
              </h3>
              <p className="text-[#A1A1AA]">{selectedImage.description}</p>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* CTA */}
      <section className="section-padding-sm bg-[#0F0F0F] border-t border-[rgba(212,175,55,0.15)]">
        <div className="container-luxury text-center">
          <h2 className="text-2xl sm:text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4">
            Ready to Create Your Own Story?
          </h2>
          <p className="text-[#A1A1AA] mb-8 max-w-xl mx-auto">
            Your event could be our next masterpiece. Let's make it unforgettable.
          </p>
          <a 
            href="/booking" 
            className="btn-primary inline-block"
            data-testid="gallery-cta-btn"
          >
            Book Your Event
          </a>
        </div>
      </section>
    </main>
  );
};

export default GalleryPage;
